/**
 * Dashboard — platform overview / home screen.
 *
 * Renders a hero banner and a grid of the six core TalentScope360 modules,
 * each as a clickable card that navigates to its respective route.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

/** Static metadata for each of the six platform modules shown on the dashboard. */
const modules = [
  {
    number: '01',
    title: 'Talent Goals',
    route: '/corporate-structure',
    gradient: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #6d28d9 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    description: 'Defines the organizational structure and lays the foundation for enterprise-wide talent goals aligned to your mission and values.',
    features: ['Corporate Structure', 'Functional Structure', 'Org Alignment'],
  },
  {
    number: '02',
    title: 'Talent Strategy',
    route: '/core-competencies',
    gradient: 'linear-gradient(135deg, #2d4a9e 0%, #4a6bb5 50%, #3d5ab0 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    description: 'Establishes the competency framework that drives all talent management initiatives across the organization.',
    features: ['Competency Framework', 'Enterprise Talent Map', 'Strategy Alignment'],
  },
  {
    number: '03',
    title: 'Talent Planning',
    route: '/role-competencies',
    gradient: 'linear-gradient(135deg, #0d7377 0%, #14a098 50%, #108a8f 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    description: "Derived from the organization's mission and values, aiming to build a high-performing, adaptable, and sustainable workforce.",
    features: ['Role Competency Models', 'Individual Planning', 'Goal Alignment'],
  },
  {
    number: '04',
    title: 'Talent Review',
    route: '/evaluation',
    gradient: 'linear-gradient(135deg, #b8860b 0%, #daa520 50%, #c8981a 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    description: "Focuses on evaluating individual competence, role fit, and growth readiness using the framework's four competency levels.",
    features: ['Competency Assessment', 'Role Fit Analysis', 'Growth Readiness'],
  },
  {
    number: '05',
    title: 'Talent Performance',
    route: '/corporate-performance',
    gradient: 'linear-gradient(135deg, #9b2c2c 0%, #c53030 50%, #b83838 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    description: 'Centers on evaluating and managing results through a competency-based performance management approach.',
    features: ['Performance Metrics', 'Competency Review', 'Results Management'],
  },
  {
    number: '06',
    title: 'Talent Development',
    route: '/corporate-talent-plan',
    gradient: 'linear-gradient(135deg, #9a3412 0%, #ea580c 50%, #c2410c 100%)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    description: 'Translates talent strategy and review outcomes into structured development plans at corporate, functional, and individual levels.',
    features: ['Corporate Talent Plan', 'Functional Talent Plan', 'Individual Talent Plan'],
  },
];

/** Main dashboard component — module overview grid + hero banner. */
const PerformanceDashboard = () => {
  const navigate = useNavigate();

  return (
    <div className="main-dashboard">
      <div className="main-dashboard__hero">
        <div className="main-dashboard__hero-inner">
          <div className="main-dashboard__hero-badge">Platform Overview</div>
          <h1 className="main-dashboard__title">Welcome to TalentScope360</h1>
          <p className="main-dashboard__subtitle">
            A comprehensive competence-based talent management platform driving performance excellence across your organization.
          </p>
          <div className="main-dashboard__hero-stats">
            <div className="main-dashboard__stat">
              <span className="main-dashboard__stat-number">6</span>
              <span className="main-dashboard__stat-label">Core Modules</span>
            </div>
            <div className="main-dashboard__stat-divider" />
            <div className="main-dashboard__stat">
              <span className="main-dashboard__stat-number">360°</span>
              <span className="main-dashboard__stat-label">Talent View</span>
            </div>
            <div className="main-dashboard__stat-divider" />
            <div className="main-dashboard__stat">
              <span className="main-dashboard__stat-number">∞</span>
              <span className="main-dashboard__stat-label">Scalable</span>
            </div>
          </div>
        </div>
        <div className="main-dashboard__hero-orb main-dashboard__hero-orb--1" />
        <div className="main-dashboard__hero-orb main-dashboard__hero-orb--2" />
        <div className="main-dashboard__hero-orb main-dashboard__hero-orb--3" />
      </div>

      <div className="main-dashboard__body">
        <div className="main-dashboard__section-header">
          <h2 className="main-dashboard__section-title">Platform Modules</h2>
          <p className="main-dashboard__section-desc">Navigate through TalentScope360's six integrated talent management modules</p>
        </div>

        <div className="main-dashboard__cards">
          {modules.map((mod) => (
            <div
              key={mod.number}
              className="main-dashboard__card"
              style={{ background: mod.gradient }}
            >
              <div className="main-dashboard__card-orb" />
              <div className="main-dashboard__card-top">
                <div className="main-dashboard__card-icon">{mod.icon}</div>
                <span className="main-dashboard__card-num">{mod.number}</span>
              </div>
              <h3 className="main-dashboard__card-title">{mod.title}</h3>
              <p className="main-dashboard__card-text">{mod.description}</p>
              <ul className="main-dashboard__card-tags">
                {mod.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <div className="main-dashboard__card-footer">
                <button
                  className="main-dashboard__card-cta"
                  onClick={() => navigate(mod.route)}
                >
                  Explore module →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
